import * as z from 'zod'

import { filingStatusSchema } from '#dictionary/filingStatuses'

import raw from './standardDeductions.json'

// The federal standard deduction — the flat amount subtracted from adjusted
// gross income before regular brackets apply. The IRS publishes a table each
// year with a base amount per filing status plus additional amounts for age
// 65+ or blind qualifiers. Source values were retroactively adjusted for the
// One Big Beautiful Bill Act (OBBBA, mid-2025).
export const standardDeductionSchema = z.object({
  standardDeductionGroup: z.union([
    z.literal(0),
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
  ]),
  filingStatus: filingStatusSchema,
  year: z.int().min(2025),
  value: z.number().nonnegative(),
})

export type StandardDeduction = z.infer<typeof standardDeductionSchema>

// The group tier a household falls into — sum of the "additional
// deduction" qualifiers (blind, 65+) across household members. Maps
// 1:1 to the `standardDeductionGroup` column in source rows.
export type StandardDeductionGroup = StandardDeduction['standardDeductionGroup']

// The subset a single member can contribute (their own blind + senior
// qualifiers). Household group = sum of member groups. Split from
// StandardDeductionGroup so a single-member return type doesn't lie
// about being able to reach 3 or 4.
export type StandardDeductionMemberGroup = 0 | 1 | 2

// Annual growth rate applied to project standard-deduction values from the
// latest source year forward. Set to 0.014 (~1.4% CPI-based inflation).
//
// TODO: This is hard-coded on Gabriel's recommendation. Consider deriving a
// smarter number from the actual historical year-over-year growth of the
// published IRS values across the past N years, rather than baking in a
// single CPI-flavored constant.
export const STANDARD_DEDUCTIONS_GROWTH_RATE = 0.014

export const STANDARD_DEDUCTIONS: readonly StandardDeduction[] = z
  .array(standardDeductionSchema)
  .parse(raw)
